import { UserCog } from 'lucide-react';

import { GearIcon } from '~/components/logos/GearIcon';
import { cn } from '~/utils/cn';

import { CheckpointStatus, type TimelineCheckpoint } from '../../types/experiment.types';
import { canDownloadReadout, isResultsReadoutCheckpoint } from '../../utils/readout-checkpoint';

import { ExperimentCheckpointStatusBadge } from './experiment-checkpoint-status-badge';
import { ExperimentReadoutDownloads } from './experiment-readout-downloads';
import { RevenueSourceTag } from './revenue-source-tag';

const iconComponents = {
  gear: GearIcon,
  people: UserCog,
} as const;

const iconSizes = {
  gear: 'h-6 w-6',
  people: 'h-5 w-5',
} as const;

const iconStyles: Record<CheckpointStatus, string> = {
  [CheckpointStatus.PASSED]: 'bg-primary text-primary-foreground',
  [CheckpointStatus.IN_PROGRESS]: 'bg-warning text-primary-foreground',
  [CheckpointStatus.PENDING]: 'bg-muted text-muted-foreground',
  [CheckpointStatus.WARNING]: 'bg-warning text-primary-foreground',
  [CheckpointStatus.FAILED]: 'bg-destructive text-primary-foreground',
};

interface Props {
  checkpoint: TimelineCheckpoint;
  /** Owning experiment — needed by the Day 28 row's readout download actions. */
  experimentId: string;
  isFirst: boolean;
  isLast: boolean;
  topLineCompleted: boolean;
  bottomLineCompleted: boolean;
}

export function ExperimentTimelineItem({
  checkpoint,
  experimentId,
  isFirst,
  isLast,
  topLineCompleted,
  bottomLineCompleted,
}: Props) {
  const Icon = iconComponents[checkpoint.icon];
  // The Day 28 row carries the readout downloads; they stay disabled until that
  // checkpoint passes, so the affordance is visible before the data exists.
  const isReadout = isResultsReadoutCheckpoint(checkpoint);

  return (
    <div
      className={cn(
        'relative grid grid-cols-[88px_minmax(0,2fr)_minmax(0,1fr)_minmax(0,3fr)] items-stretch gap-4 px-6 py-6',
        !isLast &&
          'after:bg-border after:absolute after:right-6 after:bottom-0 after:left-32 after:h-px after:content-[""]',
      )}
    >
      {/* Icon + connecting line (stretches full row height) */}
      <div className="relative flex items-center justify-center self-stretch">
        {!isFirst && (
          <span
            className={cn(
              'absolute -top-6 bottom-[calc(50%+28px)] left-1/2 w-0.5 -translate-x-1/2',
              topLineCompleted ? 'bg-primary' : 'bg-border',
            )}
          />
        )}
        {!isLast && (
          <span
            className={cn(
              'absolute top-[calc(50%+28px)] -bottom-6 left-1/2 w-0.5 -translate-x-1/2',
              bottomLineCompleted ? 'bg-primary' : 'bg-border',
            )}
          />
        )}
        <span
          className={cn(
            'relative flex h-12 w-12 items-center justify-center rounded-full',
            iconStyles[checkpoint.status],
            checkpoint.status === CheckpointStatus.PENDING && 'border',
          )}
        >
          <Icon className={iconSizes[checkpoint.icon]} aria-hidden="true" />
        </span>
      </div>

      {/* Checkpoint */}
      <div className="min-w-0 self-center">
        <p className="text-muted-foreground text-sm">{checkpoint.dayLabel}</p>
        <p className="text-foreground font-medium">{checkpoint.name}</p>
      </div>

      {/* Status */}
      <div className="self-center">
        <ExperimentCheckpointStatusBadge status={checkpoint.status} />
      </div>

      {/* Details — plus the readout downloads on the Day 28 row. Kept inside
          this column so the shared 4-column grid template (header, rows,
          skeleton, TimelineRowFrame) stays unchanged. */}
      <div className="flex flex-col gap-3 self-center">
        <p className="text-muted-foreground text-sm leading-relaxed">
          {checkpoint.details}
          {checkpoint.revenueSource && (
            <RevenueSourceTag source={checkpoint.revenueSource} className="ml-1.5" />
          )}
        </p>
        {isReadout && (
          <ExperimentReadoutDownloads
            experimentId={experimentId}
            isAvailable={canDownloadReadout(checkpoint)}
          />
        )}
      </div>
    </div>
  );
}
